/**
 * Private profile helpers — T1 (LAUNCH_PLAN.md).
 *
 * PII (email/dob/firstName/lastName/region/city) lives under
 *   <kind>/{uid}/private/profile
 * with rule `read: isOwner(uid) || isAdmin(); write: isOwner(uid)`.
 *
 * Sub-step status (see LAUNCH_PLAN.md):
 *   T1a — rule path opened + these helpers added.        (this file)
 *   T1b — dual-write at sign-up (GetStarted).            (next)
 *   T1c — dual-read in UserContext / profile / admin.    (next)
 *   T1d — backfill migration script.                     (next)
 *   T1e — strip PII from parent docs.                    (last)
 */

import { FirebaseError } from 'firebase/app';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

export type PrivateProfileKind = 'users' | 'creators' | 'viewers';

const PRIVATE_DOC_ID = 'profile';

export interface PrivateProfileFields {
  email?: string | null;
  dob?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  region?: string | null;
  city?: string | null;
}

export interface PrivateProfileDoc extends PrivateProfileFields {
  updatedAt?: unknown;
}

function privateRef(uid: string, kind: PrivateProfileKind) {
  return doc(db, kind, uid, 'private', PRIVATE_DOC_ID);
}

/**
 * Reads <kind>/{uid}/private/profile.
 * Returns null on permission-denied (the expected non-owner / non-admin path)
 * so callers can fall back to public parent-doc fields without try/catch.
 * Other Firebase / network errors propagate.
 */
export async function getPrivateProfile(
  uid: string,
  kind: PrivateProfileKind
): Promise<PrivateProfileDoc | null> {
  if (!uid) return null;
  try {
    const snap = await getDoc(privateRef(uid, kind));
    return snap.exists() ? (snap.data() as PrivateProfileDoc) : null;
  } catch (err) {
    if (err instanceof FirebaseError && err.code === 'permission-denied') {
      return null;
    }
    throw err;
  }
}

/**
 * Batch variant for admin list views (paginated user tables, recent-active lists).
 * Issues N parallel reads via getPrivateProfile and returns a uid → doc map.
 * Reads that fail with permission-denied / non-existent yield null entries —
 * callers should fall back to parent values.
 */
export async function getPrivateProfilesBatch(
  uids: readonly string[],
  kind: PrivateProfileKind
): Promise<Map<string, PrivateProfileDoc | null>> {
  const entries = await Promise.all(
    uids.map(async (uid) => [uid, await getPrivateProfile(uid, kind)] as const)
  );
  return new Map(entries);
}

/**
 * Writes <kind>/{uid}/private/profile (owner-only by rule).
 * Defaults to merge=true so callers can patch individual fields safely.
 * Stamps `updatedAt: serverTimestamp()` on every write.
 */
export async function setPrivateProfile(
  uid: string,
  kind: PrivateProfileKind,
  data: PrivateProfileFields,
  options: { merge?: boolean } = {}
): Promise<void> {
  if (!uid) throw new Error('setPrivateProfile: uid is required');
  const merge = options.merge !== false;
  await setDoc(
    privateRef(uid, kind),
    { ...data, updatedAt: serverTimestamp() },
    { merge }
  );
}
